// Spacing is a closed scale. A feature passes a number from it, never a
// length. Every class is written out in full so Tailwind can see it.

export type Space = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24;

export type Responsive<T> = T | { readonly base?: T; readonly sm?: T; readonly md?: T; readonly lg?: T };

type Breakpoint = "base" | "sm" | "md" | "lg";

export const GAP: Readonly<Record<Breakpoint, Readonly<Record<Space, string>>>> = {
  base: {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
    8: "gap-8",
    10: "gap-10",
    12: "gap-12",
    16: "gap-16",
    20: "gap-20",
    24: "gap-24",
  },
  sm: {
    0: "sm:gap-0",
    1: "sm:gap-1",
    2: "sm:gap-2",
    3: "sm:gap-3",
    4: "sm:gap-4",
    5: "sm:gap-5",
    6: "sm:gap-6",
    8: "sm:gap-8",
    10: "sm:gap-10",
    12: "sm:gap-12",
    16: "sm:gap-16",
    20: "sm:gap-20",
    24: "sm:gap-24",
  },
  md: {
    0: "md:gap-0",
    1: "md:gap-1",
    2: "md:gap-2",
    3: "md:gap-3",
    4: "md:gap-4",
    5: "md:gap-5",
    6: "md:gap-6",
    8: "md:gap-8",
    10: "md:gap-10",
    12: "md:gap-12",
    16: "md:gap-16",
    20: "md:gap-20",
    24: "md:gap-24",
  },
  lg: {
    0: "lg:gap-0",
    1: "lg:gap-1",
    2: "lg:gap-2",
    3: "lg:gap-3",
    4: "lg:gap-4",
    5: "lg:gap-5",
    6: "lg:gap-6",
    8: "lg:gap-8",
    10: "lg:gap-10",
    12: "lg:gap-12",
    16: "lg:gap-16",
    20: "lg:gap-20",
    24: "lg:gap-24",
  },
};

export const PADDING: Readonly<Record<Space, string>> = {
  0: "p-0",
  1: "p-1",
  2: "p-2",
  3: "p-3",
  4: "p-4",
  5: "p-5",
  6: "p-6",
  8: "p-8",
  10: "p-10",
  12: "p-12",
  16: "p-16",
  20: "p-20",
  24: "p-24",
};

export type Columns = 1 | 2 | 3 | 4 | 5 | 6 | 12;

export const COLUMNS: Readonly<Record<Breakpoint, Readonly<Record<Columns, string>>>> = {
  base: {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    12: "grid-cols-12",
  },
  sm: {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
    4: "sm:grid-cols-4",
    5: "sm:grid-cols-5",
    6: "sm:grid-cols-6",
    12: "sm:grid-cols-12",
  },
  md: {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
    6: "md:grid-cols-6",
    12: "md:grid-cols-12",
  },
  lg: {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
    12: "lg:grid-cols-12",
  },
};

/** Resolves a responsive value through a per-breakpoint class table. */
export function responsive<T extends string | number>(
  value: Responsive<T> | undefined,
  table: Readonly<Record<Breakpoint, Readonly<Record<T, string>>>>,
): string[] {
  if (value === undefined) return [];
  if (typeof value !== "object") return [table.base[value]];
  const classes: string[] = [];
  for (const breakpoint of ["base", "sm", "md", "lg"] as const) {
    const step = value[breakpoint];
    if (step !== undefined) classes.push(table[breakpoint][step]);
  }

  return classes;
}
